// ─────────────────────────────────────────────────────────────────────────────
// vault_fs_adapter.ts — File System Access API backend, native-format edition.
//
// Reads and writes the same on-disk layout that `run_pwa.py` uses, so a
// folder can be shared transparently between browser-FSA mode and the
// desktop server. That's the whole point of the user-picked folder: the
// OS-level sync agent (OneDrive/Drive/Dropbox) carries the same files
// between devices, regardless of which runtime opened the journal.
//
// On-disk layout (inside the picked folder):
//
//   <folder>/
//     <stem>.ox.json           one file per page, full payload inline
//                              ($schema "ox.page/1": id, title, blocks,
//                              arrows, strokes, …)
//     .ox/journal.json         notebook metadata
//     .ox/session.json         last currentPagePath
//     .ox/actions.jsonl        append-only event log
//     .ox/settings.json        user preferences (PWA-only)
//
// Strategy: a tiny module-level cache holds the parsed page files for the
// currently-active handle. `loadIndex` populates it via a single directory
// walk; subsequent `loadPageStrokes` lookups hit the cache instead of
// re-walking the dir, keeping init O(n) instead of O(n²).
// ─────────────────────────────────────────────────────────────────────────────

import type { GrimoireAction } from '@shared/grimoire_action';
import type { NotebookIndex, SerialPageEntry, StrokeData } from './persistence';

const DOT_OX = '.ox';
const FILE_JOURNAL = 'journal.json';
const FILE_SESSION = 'session.json';
const FILE_ACTIONS = 'actions.jsonl';
const FILE_SETTINGS = 'settings.json';
const PAGE_EXT = '.ox.json';

// ── Native shapes ───────────────────────────────────────────────────────────

interface NativePageFile {
  $schema?: string;
  id: string;
  title: string;
  sectionId?: string;
  color?: string;
  isSectionHeader?: boolean;
  bookmark?: SerialPageEntry['bookmark'];
  densityLevel?: number;
  defaultLayer?: number;
  createdAt?: number;
  updatedAt?: number;
  blocks?: unknown[];
  arrows?: unknown[];
  strokes?: StrokeData[];
}

export interface NativeJournalMeta {
  $schema?: string;
  id?: string;
  title?: string;
  createdAt?: number;
  updatedAt?: number;
  coverSeed?: number;
}

export interface NativeSession {
  currentPagePath?: string;
  savedAt?: number;
}

// ── Module-level cache ─────────────────────────────────────────────────────

let _activeHandle: FileSystemDirectoryHandle | null = null;
/** pageId -> { filename within active folder, parsed file contents } */
let _pageCache: Map<string, { filename: string; data: NativePageFile }> | null = null;

function ensureCacheForHandle(handle: FileSystemDirectoryHandle): void {
  if (_activeHandle !== handle) {
    _activeHandle = handle;
    _pageCache = null;
  }
}

// ── Low-level helpers ──────────────────────────────────────────────────────

async function readJsonFile<T>(file: FileSystemFileHandle): Promise<T | null> {
  const f = await file.getFile();
  if (f.size === 0) return null;
  return JSON.parse(await f.text()) as T;
}

async function writeJsonFile(file: FileSystemFileHandle, value: unknown): Promise<void> {
  const w = await file.createWritable();
  await w.write(JSON.stringify(value, null, 2));
  await w.close();
}

async function getOrCreateDotOx(handle: FileSystemDirectoryHandle): Promise<FileSystemDirectoryHandle> {
  return handle.getDirectoryHandle(DOT_OX, { create: true });
}

async function maybeGetDotOx(handle: FileSystemDirectoryHandle): Promise<FileSystemDirectoryHandle | null> {
  try {
    return await handle.getDirectoryHandle(DOT_OX);
  } catch (e) {
    if ((e as DOMException)?.name === 'NotFoundError') return null;
    throw e;
  }
}

async function tryReadJson<T>(dir: FileSystemDirectoryHandle, name: string): Promise<T | null> {
  try {
    const f = await dir.getFileHandle(name);
    return await readJsonFile<T>(f);
  } catch (e) {
    if ((e as DOMException)?.name === 'NotFoundError') return null;
    throw e;
  }
}

async function listPageFilenames(handle: FileSystemDirectoryHandle): Promise<string[]> {
  const out: string[] = [];
  // FileSystemDirectoryHandle has an async iterator on `.entries()` per spec.
  // TypeScript's lib.dom may not have it typed; assert.
  const iter = (handle as unknown as { entries(): AsyncIterable<[string, FileSystemHandle]> }).entries();
  for await (const [name, entry] of iter) {
    if (entry.kind !== 'file') continue;
    if (name.startsWith('.')) continue;
    if (!name.toLowerCase().endsWith(PAGE_EXT)) continue;
    out.push(name);
  }
  out.sort();
  return out;
}

function safeStem(title: string, fallback: string): string {
  const t = (title || fallback).replace(/[\\/:*?"<>| ]+/g, '-').trim();
  return t || fallback;
}

// ── Native-format helpers (used by standalone_host's init path) ───────────

export async function readJournalMeta(
  handle: FileSystemDirectoryHandle,
): Promise<NativeJournalMeta | null> {
  const dotOx = await maybeGetDotOx(handle);
  return dotOx ? tryReadJson<NativeJournalMeta>(dotOx, FILE_JOURNAL) : null;
}

export async function writeJournalMeta(
  handle: FileSystemDirectoryHandle,
  meta: NativeJournalMeta,
): Promise<void> {
  const dotOx = await getOrCreateDotOx(handle);
  const fh = await dotOx.getFileHandle(FILE_JOURNAL, { create: true });
  await writeJsonFile(fh, { $schema: 'ox.journal/1', ...meta, updatedAt: Date.now() });
}

export async function readSession(
  handle: FileSystemDirectoryHandle,
): Promise<NativeSession | null> {
  const dotOx = await maybeGetDotOx(handle);
  return dotOx ? tryReadJson<NativeSession>(dotOx, FILE_SESSION) : null;
}

export async function writeSession(
  handle: FileSystemDirectoryHandle,
  session: NativeSession,
): Promise<void> {
  const dotOx = await getOrCreateDotOx(handle);
  const fh = await dotOx.getFileHandle(FILE_SESSION, { create: true });
  await writeJsonFile(fh, { ...session, savedAt: Date.now() });
}

/** Walk the folder once, parse every `*.ox.json`, populate the cache. */
export async function readAllPages(
  handle: FileSystemDirectoryHandle,
): Promise<Array<{ filename: string; data: NativePageFile }>> {
  ensureCacheForHandle(handle);
  const filenames = await listPageFilenames(handle);
  const out: Array<{ filename: string; data: NativePageFile }> = [];
  const cache = new Map<string, { filename: string; data: NativePageFile }>();
  for (const fn of filenames) {
    try {
      const fh = await handle.getFileHandle(fn);
      const data = await readJsonFile<NativePageFile>(fh);
      if (!data || !data.id) continue;
      out.push({ filename: fn, data });
      cache.set(data.id, { filename: fn, data });
    } catch (e) {
      console.warn('[vault] skip malformed page', fn, e);
    }
  }
  _pageCache = cache;
  return out;
}

async function getCacheEntry(
  handle: FileSystemDirectoryHandle,
  pageId: string,
): Promise<{ filename: string; data: NativePageFile } | null> {
  ensureCacheForHandle(handle);
  if (!_pageCache) await readAllPages(handle);
  return _pageCache?.get(pageId) ?? null;
}

// ── Persistence-style facade (consumed by vault_dispatch) ──────────────────

export async function loadIndex(handle: FileSystemDirectoryHandle): Promise<NotebookIndex | null> {
  ensureCacheForHandle(handle);
  const meta = await readJournalMeta(handle).catch(() => null);
  const session = await readSession(handle).catch(() => null);
  const pages = await readAllPages(handle);

  if (!meta && pages.length === 0) return null;

  const serial: SerialPageEntry[] = pages.map(({ data }, i) => {
    const blocks = Array.isArray(data.blocks) ? data.blocks : [];
    const arrows = Array.isArray(data.arrows) ? data.arrows : [];
    return {
      id: data.id,
      index: i,
      title: data.title || 'Untitled',
      color: data.color,
      sectionId: data.sectionId,
      isSectionHeader: data.isSectionHeader,
      densityLevel: data.densityLevel ?? 0,
      bookmark: data.bookmark,
      blockCount: blocks.length,
      arrowCount: arrows.length,
      updatedAt: data.updatedAt ?? Date.now(),
      blocks,
      arrows,
    };
  });

  let currentPageId: string | undefined;
  if (session?.currentPagePath) {
    const found = pages.find(p => p.filename === session.currentPagePath);
    if (found) currentPageId = found.data.id;
  }

  return {
    schema: '1.1',
    meta: {
      id: meta?.id || ('fsa-' + (handle.name || 'vault')),
      title: meta?.title || handle.name || 'Vault',
      createdAt: meta?.createdAt || Date.now(),
      updatedAt: meta?.updatedAt || Date.now(),
      coverSeed: meta?.coverSeed ?? Math.floor(Math.random() * 1e9),
    },
    pages: serial,
    sections: [],
    currentPageId: currentPageId ?? serial[0]?.id,
    savedAt: Date.now(),
  };
}

export async function saveIndex(
  handle: FileSystemDirectoryHandle,
  index: NotebookIndex,
): Promise<void> {
  await writeJournalMeta(handle, {
    id: index.meta.id,
    title: index.meta.title,
    createdAt: index.meta.createdAt,
    updatedAt: index.meta.updatedAt,
    coverSeed: index.meta.coverSeed,
  });
  if (index.currentPageId) {
    const entry = await getCacheEntry(handle, index.currentPageId);
    if (entry) await writeSession(handle, { currentPagePath: entry.filename });
  }
}

export async function appendAction(
  handle: FileSystemDirectoryHandle,
  action: GrimoireAction,
): Promise<void> {
  const dotOx = await getOrCreateDotOx(handle);
  const fh = await dotOx.getFileHandle(FILE_ACTIONS, { create: true });
  let existing = '';
  try {
    const file = await fh.getFile();
    existing = await file.text();
  } catch { /* file just created */ }
  const w = await fh.createWritable();
  await w.write(existing + JSON.stringify(action) + '\n');
  await w.close();
}

export async function loadAllActions(
  _handle: FileSystemDirectoryHandle,
): Promise<GrimoireAction[]> {
  // Page state lives inline in page files; we don't replay the action log.
  // Returning [] makes _replayActions a no-op for FSA mode, which is what
  // we want — pages are already fully loaded via loadIndex.
  return [];
}

export async function loadPageStrokes(
  handle: FileSystemDirectoryHandle,
  pageId: string,
): Promise<StrokeData[]> {
  const entry = await getCacheEntry(handle, pageId);
  if (!entry) return [];
  return entry.data.strokes ?? [];
}

export async function savePageStrokes(
  handle: FileSystemDirectoryHandle,
  pageId: string,
  strokes: StrokeData[],
): Promise<void> {
  ensureCacheForHandle(handle);
  let entry = await getCacheEntry(handle, pageId);
  if (!entry) {
    const filename = `${safeStem(pageId, 'page')}${PAGE_EXT}`;
    const data: NativePageFile = {
      $schema: 'ox.page/1',
      id: pageId,
      title: pageId,
      densityLevel: 0,
      createdAt: Date.now(),
      blocks: [],
      arrows: [],
    };
    entry = { filename, data };
    if (_pageCache) _pageCache.set(pageId, entry);
  }
  entry.data.strokes = strokes;
  entry.data.updatedAt = Date.now();
  entry.data.$schema = entry.data.$schema || 'ox.page/1';
  const fh = await handle.getFileHandle(entry.filename, { create: true });
  await writeJsonFile(fh, entry.data);
}

export async function saveAllStrokes(
  handle: FileSystemDirectoryHandle,
  pageStrokes: Map<string, Map<string, StrokeData>>,
): Promise<void> {
  for (const [pageId, strokeMap] of pageStrokes) {
    await savePageStrokes(handle, pageId, [...strokeMap.values()]);
  }
}

export async function saveSettings(
  handle: FileSystemDirectoryHandle,
  settings: Record<string, unknown>,
): Promise<void> {
  const dotOx = await getOrCreateDotOx(handle);
  const fh = await dotOx.getFileHandle(FILE_SETTINGS, { create: true });
  await writeJsonFile(fh, settings);
}

export async function loadSettings(
  handle: FileSystemDirectoryHandle,
): Promise<Record<string, unknown> | null> {
  const dotOx = await maybeGetDotOx(handle);
  return dotOx ? tryReadJson<Record<string, unknown>>(dotOx, FILE_SETTINGS) : null;
}

export async function hasNotebook(
  handle: FileSystemDirectoryHandle,
): Promise<boolean> {
  return (await loadIndex(handle)) !== null;
}

export async function clearAll(_handle: FileSystemDirectoryHandle): Promise<void> {
  // Destructive operations on the user's filesystem need explicit consent
  // we don't yet collect. Files can be deleted manually if desired.
  console.warn('[vault] clearAll is a no-op in folder mode; delete files manually.');
}
